import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  formatPhoneNumber,
  QA_STATUSES,
  QA_CATEGORIES,
  type QAStatusType,
  type QACategoryType,
  type QAFeedbackList,
  type QAFeedbackListQueryParams,
  type QAEntityType,
} from '@intone/shared';

import { DataTable, type Column, type FilterConfig } from '@/shared/ui/DataTable';
import { getStatusColor } from '@/shared/lib/colors';
import { formatTimeCreated } from '@/shared/lib/date';
import { Badge } from '@/components/ui/badge';
import { useQAFeedbackList } from '@/entities/qa/qaFeedback';
import { CallDetails } from '@/widgets/callDetails';
import { ChatConversationDetails } from '@/widgets/chatConversationDetails';
import { CampaignSelector } from '@/components/features/CampaignSelector';
import { useCampaignSelection } from '@/entities/campaign';
import { QASummaryStats } from './QASummaryStats';

export function QAPage() {
  const navigate = useNavigate()
  const { callId } = useParams<{ callId?: string }>()
  const { campaignId, setCampaignId, campaigns, selectedCampaign } = useCampaignSelection()

  const isText = selectedCampaign?.telephony === 'custom';
  const entityType: QAEntityType = isText ? 'conversation' : 'call';

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = 25;
  // Sorting
  const [sortField, setSortField] = useState<string>('created_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [timeFilter, setTimeFilter] = useState<string>('all')

  // Build query params
  const queryParams: QAFeedbackListQueryParams = useMemo(() => ({
    page_id: currentPage,
    page_size: pageSize,
    order_by: sortField as QAFeedbackListQueryParams['order_by'],
    order_type: sortDirection,
    ...(statusFilter !== 'all' && { status: statusFilter as QAStatusType}),
    ...(categoryFilter !== 'all' && { category: categoryFilter as QACategoryType }),
    ...(timeFilter !== 'all' && { time_filter: timeFilter as QAFeedbackListQueryParams['time_filter'] }),
    ...(campaignId && { campaign_id: campaignId }),
    entity_type: entityType,
  }), [currentPage, pageSize, sortField, sortDirection, statusFilter, categoryFilter, timeFilter, campaignId, entityType])

  const { data, isLoading, isFetching, error } = useQAFeedbackList(queryParams);
  const initialLoading = isLoading && !data;

  const rows: QAFeedbackList[] = data?.feedbacks || [];
  const totalCount = data?.totalCount || 0;

  // Build filters configuration for DataTable
  const filters: FilterConfig[] = useMemo(
    () => [
      {
        type: 'select',
        key: 'status',
        label: 'Status',
        options: [
          { value: 'all', label: 'All Statuses' },
          ...Object.values(QA_STATUSES).map((status) => ({
            value: status,
            label: status,
          })),
        ],
        placeholder: 'All Statuses',
        noFilterValue: 'all',
      },
      {
        type: 'select',
        key: 'category',
        label: 'Category',
        options: [
          { value: 'all', label: 'All Categories' },
          ...Object.values(QA_CATEGORIES).map((cat) => ({
            value: cat,
            label: cat,
          })),
        ],
        placeholder: 'All Categories',
        noFilterValue: 'all',
      },
      {
        type: 'select',
        key: 'time',
        label: 'Time',
        placeholder: 'All Time',
        noFilterValue: 'all',
        options: [
          { value: 'all', label: 'All Time' },
          { value: 'today', label: 'Today' },
          { value: 'week', label: 'This Week' },
        ],
      },
    ],
    []
  )

  // Filter values for DataTable
  const filterValues = useMemo(
    () => ({
      status: statusFilter,
      category: categoryFilter,
      time: timeFilter,
    }),
    [statusFilter, categoryFilter, timeFilter]
  )

  // Handle filter change from DataTable
  const handleFilterChange = (key: string, value: string) => {
    switch (key) {
      case 'status':
        setStatusFilter(value)
        break
      case 'category':
        setCategoryFilter(value)
        break
      case 'time':
        setTimeFilter(value)
        break
    }
  }

  // Define columns for DataTable
  const columns: Column<QAFeedbackList>[] = useMemo(
    () => [
      {
        header: 'Customer',
        accessorKey: 'customer',
        sortField: 'customer',
        sortable: !!rows.length,
        cell: (value: string) => (
          <div className="whitespace-nowrap text-detail">
            {isText ? (value || '-') : formatPhoneNumber(value)}
          </div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        sortField: 'status',
        sortable: !!rows.length,
        cell: (value: QAStatusType) => {
          return (
            <div className="max-w-[120px]">
              <Badge
                variant="outline"
                className={`whitespace-normal break-words ${getStatusColor(value)}`}
              >
                {value}
              </Badge>
            </div>
          )
        },
      },
      {
        header: 'Category',
        accessorKey: 'category',
        sortField: 'category',
        sortable: !!rows.length,
        cell: (value: string) => {
          return value ? (
            <div className="max-w-[150px]">
              <Badge variant="outline" className="whitespace-normal break-words">
                {value}
              </Badge>
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )
        },
      },
      {
        header: 'Created',
        accessorKey: 'created_time',
        sortField: 'created_time',
        sortable: !!rows.length,
        cell: (value: Date) => {
          return <div className="text-detail whitespace-nowrap">{formatTimeCreated(value)}</div>
        },
      },
    ],
    [rows, isText]
  )

  const handleRowClick = (row: QAFeedbackList) => {
    navigate(`/qa/${row.id}`)
  }

  return (
    <div className="w-full h-full p-6 overflow-y-auto">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="flex-shrink-0">
          <h1 className="text-heading font-bold text-gray-900 mb-1">Quality Assurance</h1>
          <p className="text-detail text-blue-600 bg-blue-50 border border-blue-200 rounded px-3 py-2">
            Submit up to 10 critical issues daily for review by the support team
          </p>
        </div>
        <CampaignSelector
          campaigns={campaigns}
          selectedCampaignId={campaignId}
          onCampaignChange={setCampaignId}
          className="w-full sm:w-72"
        />
      </div>

      <div className="flex flex-col gap-6">
        {/* Summary Stats */}
        <QASummaryStats entityType={entityType} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className={`lg:col-span-6 ${isFetching && !initialLoading ? 'opacity-60 transition-opacity duration-200' : 'transition-opacity duration-200'}`}>
            <DataTable<QAFeedbackList>
              columns={columns}
              data={rows}
              loading={initialLoading}
              error={error?.message}
              pagination={{currentPage, pageSize, totalCount}}
              onPageChange={setCurrentPage}
              sorting={{ sortField, sortDirection }}
              onSortChange={(sortField, sortDirection) => {
                setSortField(sortField)
                setSortDirection(sortDirection)
              }}
              filters={filters}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onRowClick={handleRowClick}
              selectedId={callId || null}
              showDownload={true}
              downloadFileName={`qa-${entityType}s.csv`}
            />
          </div>

          <div className="lg:col-span-6">
            {isText ? (
              <ChatConversationDetails conversationId={callId || null} />
            ) : (
              <CallDetails callId={callId || null} showManualQAFirst={true}/>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
